import {
  faCircleCheck,
  faCircleExclamation,
  faHand,
  faHourglassEnd,
  faPaperPlane,
  type IconDefinition,
} from "@fortawesome/free-solid-svg-icons";

/**
 * What actually happened when an operator pressed an action button.
 *
 * Until now every button showed the same green check the instant it dispatched,
 * which said only "the click reached our own code". It could not tell a command
 * the controller acted on from one that never left the browser, so an operator
 * watching a lift do nothing had no way to know whether to press again, fix the
 * configuration, or call someone.
 *
 * Four outcomes, deliberately distinguished:
 *
 * - `Blocked` — the send failed. Something on the click→wire path refused it:
 *   no bound message, no connection mode, no selected bank, or no socket
 *   claimed the frame. Nothing was transmitted.
 * - `Sent` — transmitted, and the action declares no expected response, so
 *   there is nothing to check against. This is NOT success: it is the honest
 *   "we sent it and cannot tell you more". An action gets here by being
 *   unconfigured, not by failing.
 * - `Confirmed` — transmitted, and every message the action nominates as proof
 *   was observed active within the verification window.
 * - `Unconfirmed` — transmitted, proof was nominated, and the window closed
 *   without seeing it. The command may still have worked; what is certain is
 *   that the controller did not report what the action said to expect.
 *
 * `Sent` and `Unconfirmed` are the pair worth keeping apart. Collapsing them
 * would let an unconfigured action look identical to one whose controller went
 * silent, which is exactly the ambiguity this exists to remove.
 */
export enum ActionOutcome {
  Blocked = "blocked",
  Sent = "sent",
  Confirmed = "confirmed",
  Unconfirmed = "unconfirmed",
  /**
   * The controller acknowledged the command and reported a DIFFERENT value than
   * the one commanded.
   *
   * Distinct from Unconfirmed, which means "no answer came". This means an
   * answer came and disagreed — the controller is reachable, understood the
   * command, and did something else. Collapsing the two would tell an operator
   * to go looking for a dead link when the link is fine.
   *
   * Only VTS's correlator can report this, because deciding it requires
   * comparing what came back against what was sent.
   */
  Contradicted = "contradicted",
  /**
   * VTS held the command back and never sent it: a channel modifier with
   * `timeout_policy` DROP gates it on an interlock (a car-call lock that must
   * confirm it has unlocked, say) and the confirmation did not arrive in time.
   *
   * Distinct from Blocked, which the browser decides before anything leaves it;
   * this is the edge deciding, after the command arrived, that sending it would
   * be unsafe. Reported by `AckOutcome.Withheld`.
   */
  Withheld = "withheld",
}

/** How one outcome is presented. */
export interface ActionOutcomeDescriptor {
  /** Heading shown in the notification. */
  title: string;
  /** One sentence saying what the operator now knows. */
  detail: string;
  icon: IconDefinition;
  /**
   * Theme class carrying the colour.
   *
   * Colour is never the only signal: each outcome also has a distinct glyph and
   * a distinct word, so the four remain separable in greyscale and to a
   * colour-blind operator.
   */
  className: string;
  /** antd notification severity. */
  severity: "success" | "info" | "warning" | "error";
}

const DESCRIPTORS: Readonly<Record<ActionOutcome, ActionOutcomeDescriptor>> = {
  [ActionOutcome.Blocked]: {
    title: "Not sent",
    detail: "The command was refused before it reached the controller.",
    icon: faCircleExclamation,
    className: "generalStyles__error",
    // WARNING, not ERROR, and deliberately: nothing failed. The system
    // correctly declined to send something it could not address, and the
    // operator's next step is to fix configuration or selection, not to report
    // a fault. `notifyCommandBlocked` — which renders this outcome — has
    // documented that reasoning since the refusal notifications were written.
    //
    // It therefore shares antd's `warning` with `Unconfirmed`, which is why
    // severity is never the only thing separating the four: the glyph, the
    // colour class and the wording all differ, so the two stay distinguishable
    // in greyscale and to a colour-blind operator.
    severity: "warning",
  },
  [ActionOutcome.Sent]: {
    title: "Sent",
    detail: "No expected response is configured for this action, so it cannot be confirmed.",
    icon: faPaperPlane,
    className: "generalStyles__info",
    severity: "info",
  },
  [ActionOutcome.Confirmed]: {
    title: "Confirmed",
    detail: "The controller reported the expected response.",
    icon: faCircleCheck,
    className: "generalStyles__success",
    severity: "success",
  },
  [ActionOutcome.Unconfirmed]: {
    title: "No confirmation",
    detail: "Sent, but the expected response did not arrive in time.",
    icon: faHourglassEnd,
    className: "generalStyles__warning",
    severity: "warning",
  },
  [ActionOutcome.Contradicted]: {
    title: "Not applied",
    icon: faCircleExclamation,
    className: "generalStyles__error",
    detail:
      "The controller acknowledged this command but reported a different value, so it did not take effect.",
    severity: "warning",
  },
  [ActionOutcome.Withheld]: {
    title: "Held back by an interlock",
    detail:
      "The edge did not send this command: the interlock it waits on did not confirm in time, " +
      "so the command was dropped rather than sent unsafely.",
    // Its own glyph and colour: a raised hand, in the withheld orange that sits
    // between the warning yellow and the error red. Warning severity, like
    // Blocked, because nothing failed — a safety gate did its job.
    icon: faHand,
    className: "generalStyles__withheld",
    severity: "warning",
  },
};

/** The presentation for an outcome. */
export function describeOutcome(outcome: ActionOutcome): ActionOutcomeDescriptor {
  return DESCRIPTORS[outcome];
}

/**
 * The default verification window, in milliseconds.
 *
 * Used when an action nominates an expected response but leaves
 * `expectedResponseTimeout` at 0. Zero on the wire means UNSET — the schema is
 * explicit that it is neither "no timeout" nor "verify immediately" — so a
 * consumer must supply its own window rather than treat 0 as an instruction.
 *
 * Five seconds is chosen against the monitoring broadcast rather than taste:
 * telemetry arrives at roughly 5Hz, so this is ~25 opportunities for the
 * expected message to appear, which tolerates a slow controller without leaving
 * an operator staring at a pending button.
 */
export const DEFAULT_VERIFICATION_WINDOW_MS = 5000;

/** Resolve the window for an action, honouring the UNSET sentinel. */
export function verificationWindowMs(expectedResponseTimeout?: number | null): number {
  if (typeof expectedResponseTimeout !== "number" || expectedResponseTimeout <= 0) {
    return DEFAULT_VERIFICATION_WINDOW_MS;
  }

  return expectedResponseTimeout * 1000;
}

/** The inputs an outcome is decided from. */
export interface OutcomeInput {
  /** False when the click→wire path refused the command. */
  delivered: boolean;
  /** Message ids the action nominates as proof. Empty means none configured. */
  expectedResponse?: readonly string[] | null;
  /** Ids observed active since the press. */
  observed?: ReadonlySet<string> | null;
  /** True once the verification window has closed. */
  windowClosed: boolean;
}

/**
 * Decide the outcome.
 *
 * Returns null while an answer is not yet available — the command is delivered,
 * proof is expected, and the window is still open. Callers render a pending
 * state for null rather than guessing, because guessing early is what makes a
 * confirmation meaningless.
 *
 * EVERY nominated message must be observed, not any: an action that lists two
 * messages is asserting both should happen, and reporting Confirmed on one of
 * them would overstate what the controller said.
 */
export function resolveOutcome(input: OutcomeInput): ActionOutcome | null {
  if (!input.delivered) return ActionOutcome.Blocked;

  const expected = (input.expectedResponse ?? []).filter((id) => id.length > 0);
  if (expected.length === 0) return ActionOutcome.Sent;

  const observed = input.observed ?? new Set<string>();
  if (expected.every((id) => observed.has(id))) return ActionOutcome.Confirmed;

  return input.windowClosed ? ActionOutcome.Unconfirmed : null;
}
